import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BusinessDetail.css';

const BusinessDetail = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);

  useEffect(() => {
    fetchDiagnostics();
  }, [businessId]);

  const fetchDiagnostics = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `/api/admin/businesses/${businessId}/visibility-diagnostics`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setDiagnostics(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    return status ? '✅' : '❌';
  };

  const getStatusClass = (status) => {
    return status ? 'status-pass' : 'status-fail';
  };

  if (loading) {
    return (
      <div className="business-detail">
        <div className="loading-spinner">Loading diagnostics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="business-detail">
        <button onClick={() => navigate(-1)} className="btn-back">← Back</button>
        <div className="error-box">{error}</div>
      </div>
    );
  }

  if (!diagnostics) {
    return null;
  }

  const { business, shops, actionItems, summary } = diagnostics;

  return (
    <div className="business-detail">
      <button onClick={() => navigate(-1)} className="btn-back">← Back to Businesses</button>
      
      <div className="detail-header">
        <h1>{business.businessName}</h1>
        <div className={`status-badge ${business.isPubliclyVisible ? 'visible' : 'hidden'}`}>
          {business.isPubliclyVisible ? '👁️ Publicly Visible' : '🔒 Hidden from Customers'}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-label">Business Status</div>
          <div className={`summary-value ${business.isPubliclyVisible ? 'success' : 'warning'}`}>
            {business.isPubliclyVisible ? 'Visible' : 'Hidden'}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Locations</div>
          <div className="summary-value">{summary.shopsTotal}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Visible Locations</div>
          <div className="summary-value success">{summary.shopsVisible}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Issues Found</div>
          <div className={`summary-value ${summary.issuesCount === 0 ? 'success' : 'warning'}`}>
            {summary.issuesCount}
          </div>
        </div>
      </div>

      {/* Action Items */}
      {actionItems && actionItems.length > 0 && (
        <div className="action-items-section">
          <h2>🔧 Action Items ({actionItems.length})</h2>
          <div className="action-items-list">
            {actionItems.map((item, idx) => (
              <div key={idx} className={`action-item priority-${item.priority}`}>
                <div className="action-level">
                  {item.level === 'business' ? '🏢 Business Level' : `📍 ${item.shopName}`}
                </div>
                <div className="action-text">{item.action}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {actionItems && actionItems.length === 0 && (
        <div className="success-banner">
          ✨ All visibility requirements met! Business and locations are live.
        </div>
      )}

      {/* Business-Level Checks */}
      <div className="diagnostics-section">
        <h2>🏢 Business-Level Requirements</h2>
        <div className="checks-grid">
          {Object.entries(business.checks).map(([key, check]) => (
            <div key={key} className={`check-item ${getStatusClass(check.status)}`}>
              <div className="check-header">
                <span className="check-icon">{getStatusIcon(check.status)}</span>
                <span className="check-name">{formatCheckName(key)}</span>
                {check.required && <span className="required-badge">Required</span>}
              </div>
              <div className="check-value">
                {typeof check.value === 'object' 
                  ? JSON.stringify(check.value, null, 2)
                  : String(check.value)
                }
              </div>
              {check.note && <div className="check-note">{check.note}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Location-Level Checks */}
      {shops && shops.length > 0 && (
        <div className="diagnostics-section">
          <h2>📍 Location-Level Requirements</h2>
          {shops.map((shop) => (
            <div key={shop.shopId} className="shop-diagnostics">
              <div className="shop-header">
                <h3>
                  {shop.shopName}
                  {shop.city && shop.state && ` - ${shop.city}, ${shop.state}`}
                </h3>
                <div className={`status-badge small ${shop.isPubliclyVisible ? 'visible' : 'hidden'}`}>
                  {shop.isPubliclyVisible ? 'Visible' : 'Hidden'}
                </div>
              </div>
              
              <div className="checks-grid">
                {Object.entries(shop.checks).map(([key, check]) => (
                  <div key={key} className={`check-item ${getStatusClass(check.status)}`}>
                    <div className="check-header">
                      <span className="check-icon">{getStatusIcon(check.status)}</span>
                      <span className="check-name">{formatCheckName(key)}</span>
                      {check.required && <span className="required-badge">Required</span>}
                    </div>
                    <div className="check-value">
                      {typeof check.value === 'object' 
                        ? JSON.stringify(check.value, null, 2)
                        : String(check.value)
                      }
                    </div>
                    {check.note && <div className="check-note">{check.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to format check names
const formatCheckName = (key) => {
  const nameMap = {
    stripeConnected: 'Stripe Connected',
    chargesEnabled: 'Charges Enabled',
    payoutsEnabled: 'Payouts Enabled',
    profileComplete: 'Profile Complete',
    ownerActive: 'Owner Active',
    businessActive: 'Business Active',
    hasActiveLocations: 'Has Active Locations',
    parentBusinessVisible: 'Parent Business Visible',
    acceptingBookings: 'Accepting Bookings',
    businessHoursConfigured: 'Business Hours Configured',
    hasActiveProfessionals: 'Has Active Professionals',
    professionalBadge: 'Professional Badge (5+ Pros)'
  };
  return nameMap[key] || key.replace(/([A-Z])/g, ' $1').trim();
};

export default BusinessDetail;
